/**
 * Componente ProductShowcase
 * Muestra productos con animaciones de entrada y efecto hover premium
 * Estilo tipo Heinz con efecto de profundidad
 */

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Tarjeta de producto con animaciones premium
 * 
 * @param {Object} props - Propiedades del componente
 * @param {string} props.image - URL de la imagen del producto
 * @param {string} props.name - Nombre del producto
 * @param {string} props.description - Descripción corta
 * @param {Function} props.onClick - Manejador de click
 * @param {string} props.className - Clases CSS adicionales
 * @param {number} props.index - Índice para stagger animation
 * @returns {JSX.Element} Tarjeta de producto
 */
const ProductCard = ({
  image,
  name,
  description,
  onClick,
  className = '',
  index = 0,
}) => {
  const cardRef = useRef(null);
  const imageRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    const card = cardRef.current;
    const img = imageRef.current;

    if (!card) return;

    // Animación de entrada con stagger
    gsap.fromTo(card,
      { 
        y: 100, 
        opacity: 0,
        rotateY: 15,
      },
      {
        y: 0,
        opacity: 1,
        rotateY: 0,
        duration: 1,
        delay: index * 0.1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: card,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
        },
      }
    );

    // Efecto hover 3D
    const handleMouseMove = (e) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const rotateX = (y - centerY) / 10;
      const rotateY = (centerX - x) / 10;

      gsap.to(card, {
        rotateX: rotateX,
        rotateY: rotateY,
        duration: 0.5,
        ease: 'power2.out',
        transformPerspective: 1000,
      });

      // La imagen se mueve en dirección opuesta para efecto de profundidad
      if (img) {
        gsap.to(img, {
          x: (x - centerX) * 0.05,
          y: (y - centerY) * 0.05,
          scale: 1.1,
          duration: 0.5,
          ease: 'power2.out',
        });
      }
    };

    const handleMouseLeave = () => {
      gsap.to(card, {
        rotateX: 0,
        rotateY: 0,
        duration: 0.7,
        ease: 'elastic.out(1, 0.5)',
      });

      if (img) {
        gsap.to(img, {
          x: 0,
          y: 0,
          scale: 1,
          duration: 0.7,
          ease: 'elastic.out(1, 0.5)',
        });
      }
    };

    card.addEventListener('mousemove', handleMouseMove);
    card.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      card.removeEventListener('mousemove', handleMouseMove);
      card.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [index]);

  return (
    <div
      ref={cardRef}
      onClick={onClick}
      className={`
        group relative cursor-pointer
        bg-white rounded-2xl overflow-hidden
        shadow-lg hover:shadow-2xl
        transition-shadow duration-500
        will-change-transform
        ${className}
      `}
      style={{ 
        transformStyle: 'preserve-3d',
        perspective: '1000px',
      }}
    >
      {/* Imagen del producto */}
      <div className="relative overflow-hidden aspect-square bg-gradient-to-br from-intercasa-bone to-white">
        <img
          ref={imageRef}
          src={image}
          alt={name}
          className="w-full h-full object-contain p-6 will-change-transform"
        />
        
        {/* Efecto de brillo al hover */}
        <div 
          className="absolute inset-0 bg-gradient-to-tr from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transition-opacity duration-500"
          style={{ transform: 'translateX(-100%) rotate(45deg)' }}
        />
      </div>

      {/* Contenido */}
      <div ref={contentRef} className="p-6 text-center">
        <h3 className="font-baskerville font-semibold text-xl md:text-2xl text-intercasa-dark mb-2 group-hover:text-intercasa-red transition-colors duration-300">
          {name}
        </h3>
        {description && (
          <p className="font-lato text-sm text-intercasa-dark/70">
            {description}
          </p>
        )}
      </div>

      {/* Línea decorativa inferior */}
      <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-red transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
    </div>
  );
};

ProductCard.propTypes = {
  image: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  onClick: PropTypes.func,
  className: PropTypes.string,
  index: PropTypes.number,
};

/**
 * Grid de productos con showcase inmersivo
 * 
 * @param {Object} props - Propiedades del componente
 * @param {Array} props.products - Array de productos
 * @param {string} props.title - Título de la sección
 * @param {string} props.subtitle - Subtítulo de la sección
 * @param {string} props.className - Clases CSS adicionales
 * @param {Function} props.onProductClick - Manejador de click en producto
 * @returns {JSX.Element} Showcase de productos
 */
const ProductShowcase = ({
  products = [],
  title,
  subtitle,
  className = '',
  onProductClick,
}) => {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);

  useEffect(() => {
    const title = titleRef.current;
    if (!title) return;

    // Animación del título
    gsap.fromTo(title,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: title,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
        },
      }
    );
  }, []);

  return (
    <section ref={sectionRef} className={`py-20 md:py-32 ${className}`}>
      {/* Header de la sección */}
      {(title || subtitle) && (
        <div ref={titleRef} className="text-center mb-16 px-4">
          {title && (
            <h2 className="font-baskerville font-bold text-4xl md:text-5xl lg:text-6xl text-intercasa-dark mb-4">
              {title}
            </h2>
          )}
          {subtitle && (
            <p className="font-lato text-lg md:text-xl text-intercasa-dark/70 max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}
        </div>
      )}

      {/* Grid de productos */}
      <div className="container mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {products.map((product, index) => (
            <ProductCard
              key={product.id || index}
              image={product.image}
              name={product.name}
              description={product.description}
              onClick={() => onProductClick && onProductClick(product)}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

ProductShowcase.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    image: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
  })),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  className: PropTypes.string,
  onProductClick: PropTypes.func,
};

export { ProductCard };
export default ProductShowcase;
